import { useState } from "react";
import ActionButton from "../atoms/ActionButton";
import ShopRow from "../molecules/ShopRow";

/**
 * ShopManagementList - 店舗・レジ一覧ビュー
 *
 * アクティブな店舗を折りたたみ可能なツリー形式で表示する。
 * 無効化済み店舗・レジはそれぞれ別セクションに表示する（再有効化はDB直接操作のみ対応）。
 *
 * @param {object[]}  shops  - 全店舗データ（GET /api/shopmanagement レスポンス）
 * @param {function}  onAdd  - 「追加」ボタンクリック時のハンドラ
 * @param {function}  onEdit - 「編集」ボタンクリック時のハンドラ (shop) => void
 */
function ShopManagementList({ shops, onAdd, onEdit }) {
  const activeShops   = shops.filter((s) => s.IsActive === 1);
  const inactiveShops = shops.filter((s) => s.IsActive !== 1);

  // 展開状態: { [ShopNo]: boolean }。初期値は全展開
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    for (const s of activeShops) init[s.ShopNo] = true;
    return init;
  });

  const toggle = (shopNo) => {
    setExpanded((prev) => ({ ...prev, [shopNo]: !prev[shopNo] }));
  };

  return (
    <div>
      {/* 追加ボタン（スクロール追従） */}
      <div className="sticky top-0 z-10 flex justify-between items-center mb-4 bg-white pb-2 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-700">店舗・レジ管理</h2>
        <ActionButton
          label="＋ 店舗を追加"
          onClick={onAdd}
          paddingClass="px-8 py-2"
          textSizeClass="text-lg font-bold"
        />
      </div>

      {/* アクティブ店舗テーブル */}
      {activeShops.length === 0 ? (
        <p className="text-gray-400 text-base">登録されている店舗はありません。</p>
      ) : (
        <table className="w-full border-collapse text-base">
          <thead>
            <tr className="border-b border-gray-300 text-left text-base text-gray-500">
              <th className="py-2 pr-2 font-normal w-6"></th>
              <th className="py-2 pr-4 font-normal w-12">No.</th>
              <th className="py-2 pr-4 font-normal">店舗名</th>
              <th className="py-2 pr-4 font-normal">建物名</th>
              <th className="py-2 pr-4 font-normal w-36">所属レジ</th>
              <th className="py-2 font-normal w-20"></th>
            </tr>
          </thead>
          <tbody>
            {activeShops.map((shop) => (
              <ShopRow
                key={shop.ShopNo}
                shop={shop}
                isExpanded={!!expanded[shop.ShopNo]}
                onToggle={() => toggle(shop.ShopNo)}
                onEdit={() => onEdit(shop)}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* 無効化済み店舗セクション */}
      {inactiveShops.length > 0 && (
        <div className="mt-8">
          <p className="text-base text-gray-400 mb-2 border-t border-gray-200 pt-4">
            無効化済みの店舗（復旧が必要な場合は開発者にご連絡ください）
          </p>
          <table className="w-full border-collapse text-base">
            <thead>
              <tr className="border-b border-gray-200 text-left text-base text-gray-400">
                <th className="py-2 pr-4 font-normal w-12">No.</th>
                <th className="py-2 pr-4 font-normal">店舗名</th>
                <th className="py-2 pr-4 font-normal">建物名</th>
                <th className="py-2 pr-4 font-normal w-36">所属レジ</th>
              </tr>
            </thead>
            <tbody>
              {inactiveShops.map((shop) => {
                const totalRegiCount = (shop.registers || []).length;
                return (
                  <tr key={shop.ShopNo} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-400 text-base">{shop.ShopNo}</td>
                    <td className="py-2 pr-4 text-gray-400">{shop.ShopName}</td>
                    <td className="py-2 pr-4 text-gray-400">{shop.BuildingName || "—"}</td>
                    <td className="py-2 pr-4 text-gray-400 text-base">
                      （{totalRegiCount} レジ）
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ShopManagementList;
